import { useCallback, useEffect, useState } from 'react';
import {
  CreditCard, Loader2, AlertCircle, Check, X, Eye, EyeOff, RefreshCw,
} from 'lucide-react';
import {
  listPaymentGateways, getEfiGateway, getMpGateway, getPaypalGateway,
  updateEfiGateway, updateMpGateway, updatePaypalGateway, testEfiGateway,
  type GatewaySummary, type GatewayProvider,
  type EfiGateway, type MpGateway, type PaypalGateway,
} from '@/shared/services/adminService';
import { useToast } from '@/shared/contexts/ToastContext';

export default function GatewaysPage() {
  const { notify } = useToast();
  const [summary, setSummary] = useState<GatewaySummary[]>([]);
  const [active, setActive] = useState<GatewayProvider>('EFI');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try { setSummary(await listPaymentGateways()); }
    catch (err: any) { setError(err?.detail || err?.message || 'Erro'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const totalEnabled = summary.filter(s => s.enabled).length;

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
            <CreditCard className="text-amber-400" size={26} />
            Gateways de Pagamento
          </h1>
          <p className="text-sm text-white/65 mt-1">
            {totalEnabled} ativo{totalEnabled !== 1 && 's'} · credenciais no banco (sem deploy)
          </p>
        </div>
      </div>

      {error && (
        <div className="surface-card rounded-xl p-4 mb-4 flex items-start gap-3 border-red-500/30">
          <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {/* Cards resumo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        {(['EFI', 'MP', 'PAYPAL'] as GatewayProvider[]).map(p => {
          const s = summary.find(x => x.provider === p);
          const isActive = active === p;
          return (
            <button
              key={p}
              onClick={() => setActive(p)}
              className={`surface-card rounded-xl p-4 text-left transition cursor-pointer
                          ${isActive ? 'border-amber-400/40 bg-amber-500/[0.04]' : 'hover:border-white/10'}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-black uppercase tracking-[0.15em] text-white/55">
                  {p}
                </span>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full
                                  ${s?.enabled
                                    ? 'bg-emerald-500/15 text-emerald-400'
                                    : 'bg-white/[0.04] text-white/40'}`}>
                  {s?.enabled ? 'ATIVO' : 'INATIVO'}
                </span>
              </div>
              <p className="text-sm font-bold text-white mt-2">{s?.displayName || '—'}</p>
              <p className="text-[11px] text-white/45 mt-1">
                {s?.ready ? 'Configurado' : 'Faltam credenciais'}
              </p>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="py-16 flex items-center justify-center text-white/40">
          <Loader2 size={20} className="animate-spin" />
        </div>
      ) : (
        <div className="surface-card rounded-xl p-6">
          {active === 'EFI'    && <EfiForm onSaved={fetchSummary} notify={notify} />}
          {active === 'MP'     && <MpForm onSaved={fetchSummary} notify={notify} />}
          {active === 'PAYPAL' && <PaypalForm onSaved={fetchSummary} notify={notify} />}
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------- EFI

function EfiForm({ onSaved, notify }: {
  onSaved: () => Promise<void>;
  notify: (kind: 'success' | 'error', msg: string) => void;
}) {
  const [data, setData] = useState<EfiGateway | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showSecret, setShowSecret] = useState(false);
  const [showHmac, setShowHmac] = useState(false);

  const [enabled, setEnabled] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [sandbox, setSandbox] = useState(true);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [certificatePath, setCertificatePath] = useState('');
  const [pixKey, setPixKey] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookHmac, setWebhookHmac] = useState('');
  const [pixExpiration, setPixExpiration] = useState(3600);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await getEfiGateway();
      setData(d);
      setEnabled(d.enabled);
      setDisplayName(d.displayName);
      setSandbox(d.sandbox);
      setClientId(d.clientId);
      setCertificatePath(d.certificatePath);
      setPixKey(d.pixKey);
      setWebhookUrl(d.webhookUrl);
      setPixExpiration(d.pixExpiration);
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro carregando EFI');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const resp = await updateEfiGateway({
        enabled, displayName, sandbox,
        clientId,
        ...(clientSecret ? { clientSecret } : {}),
        certificatePath, pixKey,
        webhookUrl,
        ...(webhookHmac ? { webhookHmac } : {}),
        pixExpiration,
      });
      // Auto-register do webhook so dispara quando URL mudou no save (backend
      // compara com o snapshot anterior). null = nao tentou; true/false = tentou.
      if (resp.webhookRegistered === true) {
        notify('success', 'EFI atualizada — webhook re-registrado na EFI');
      } else if (resp.webhookRegistered === false) {
        notify('error', `EFI salva, mas webhook NAO re-registrado: ${resp.webhookRegisterError || 'erro'}`);
      } else {
        notify('success', 'EFI atualizada');
      }
      setClientSecret('');
      setWebhookHmac('');
      await load();
      await onSaved();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro salvando');
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    try {
      const r = await testEfiGateway();
      if (r.ok) notify('success', 'EFI respondeu OK');
      else notify('error', `EFI falhou: ${r.error || 'erro'}`);
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro testando');
    } finally {
      setBusy(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-white/40" /></div>
    );
  }

  return (
    <div className="space-y-4">
      <Header
        title="EFI Pay — PIX"
        enabled={enabled}
        onToggle={setEnabled}
        ready={data.clientId && data.hasClientSecret && data.pixKey && data.certificatePath ? true : false}
      />

      <Field label="Display Name" value={displayName} onChange={setDisplayName} />

      <Row>
        <Toggle label="Sandbox (homologação)" value={sandbox} onChange={setSandbox} />
        <Field
          label="Expiração PIX (segundos)"
          value={String(pixExpiration)}
          onChange={(v) => setPixExpiration(parseInt(v || '0', 10) || 0)}
          mono
        />
      </Row>

      <Field label="Client ID" value={clientId} onChange={setClientId} mono />

      <SecretField
        label="Client Secret"
        value={clientSecret}
        onChange={setClientSecret}
        show={showSecret}
        onToggleShow={() => setShowSecret(v => !v)}
        placeholder={data.hasClientSecret ? '••••••• (mantém o atual se vazio)' : 'cole o secret aqui'}
      />

      <Field label="Caminho do certificado (.p12 / .pem)" value={certificatePath} onChange={setCertificatePath} mono />
      <Field label="Chave PIX" value={pixKey} onChange={setPixKey} mono />
      <Field label="Webhook URL" value={webhookUrl} onChange={setWebhookUrl} mono />

      <SecretField
        label="Webhook HMAC (opcional)"
        value={webhookHmac}
        onChange={setWebhookHmac}
        show={showHmac}
        onToggleShow={() => setShowHmac(v => !v)}
        placeholder={data.hasWebhookHmac ? '••••••• (mantém o atual se vazio)' : 'opcional'}
      />

      <Actions busy={busy} onSave={save} onTest={test} testLabel="Testar EFI" />
    </div>
  );
}

// ----------------------------------------------------------------- MP

function MpForm({ onSaved, notify }: {
  onSaved: () => Promise<void>;
  notify: (kind: 'success' | 'error', msg: string) => void;
}) {
  const [data, setData] = useState<MpGateway | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showToken, setShowToken] = useState(false);
  const [showHook, setShowHook] = useState(false);

  const [enabled, setEnabled] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [publicKey, setPublicKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [acceptCreditCard, setAcceptCreditCard] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await getMpGateway();
      setData(d);
      setEnabled(d.enabled);
      setDisplayName(d.displayName);
      setPublicKey(d.publicKey);
      setAcceptCreditCard(!!d.acceptCreditCard);
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro carregando MP');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      await updateMpGateway({
        enabled, displayName,
        ...(accessToken ? { accessToken } : {}),
        publicKey,
        ...(webhookSecret ? { webhookSecret } : {}),
        acceptCreditCard,
      });
      notify('success', 'MercadoPago atualizado');
      setAccessToken('');
      setWebhookSecret('');
      await load();
      await onSaved();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro salvando');
    } finally {
      setBusy(false);
    }
  };

  if (loading || !data) {
    return <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-white/40" /></div>;
  }

  return (
    <div className="space-y-4">
      <Header title="MercadoPago" enabled={enabled} onToggle={setEnabled} ready={data.hasAccessToken} />

      <Field label="Display Name" value={displayName} onChange={setDisplayName} />

      <SecretField
        label="Access Token"
        value={accessToken}
        onChange={setAccessToken}
        show={showToken}
        onToggleShow={() => setShowToken(v => !v)}
        placeholder={data.hasAccessToken ? '••••••• (mantém o atual se vazio)' : 'APP_USR-... ou TEST-...'}
      />

      <Field label="Public Key (opcional)" value={publicKey} onChange={setPublicKey} mono />

      <SecretField
        label="Webhook Secret (opcional)"
        value={webhookSecret}
        onChange={setWebhookSecret}
        show={showHook}
        onToggleShow={() => setShowHook(v => !v)}
        placeholder={data.hasWebhookSecret ? '••••••• (mantém o atual se vazio)' : 'opcional'}
      />

      <div className="pt-3 border-t border-white/[0.06]">
        <Toggle label="Aceitar cartão de crédito" value={acceptCreditCard} onChange={setAcceptCreditCard} />
        <p className="text-[11px] text-white/40 mt-1.5 leading-relaxed">
          Desativado (padrão): a preference do MercadoPago <b>exclui cartão de crédito</b> — o cliente paga só
          por PIX / saldo em conta / boleto. Ative para permitir cartão de crédito também.
        </p>
        <p className="text-[11px] mt-1.5 leading-relaxed flex items-start gap-1.5 text-amber-400/90">
          <AlertCircle size={13} className="shrink-0 mt-px" />
          <span>
            Mesmo com esta opção ligada, o cartão de crédito só é liberado para jogadores com
            <b> celular vinculado</b> (verificado por SMS). Sem celular vinculado, a recarga continua
            só por PIX / boleto — proteção anti-fraude.
          </span>
        </p>
      </div>

      <Actions busy={busy} onSave={save} />
    </div>
  );
}

// ----------------------------------------------------------------- PayPal

function PaypalForm({ onSaved, notify }: {
  onSaved: () => Promise<void>;
  notify: (kind: 'success' | 'error', msg: string) => void;
}) {
  const [data, setData] = useState<PaypalGateway | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showSecret, setShowSecret] = useState(false);

  const [enabled, setEnabled] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [mode, setMode] = useState<'Live' | 'Sandbox'>('Sandbox');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await getPaypalGateway();
      setData(d);
      setEnabled(d.enabled);
      setDisplayName(d.displayName);
      setMode(d.mode);
      setClientId(d.clientId);
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro carregando PayPal');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      await updatePaypalGateway({
        enabled, displayName, mode, clientId,
        ...(clientSecret ? { clientSecret } : {}),
      });
      notify('success', 'PayPal atualizado');
      setClientSecret('');
      await load();
      await onSaved();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro salvando');
    } finally {
      setBusy(false);
    }
  };

  if (loading || !data) {
    return <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-white/40" /></div>;
  }

  return (
    <div className="space-y-4">
      <Header title="PayPal Checkout" enabled={enabled} onToggle={setEnabled} ready={Boolean(data.clientId && data.hasClientSecret)} />

      <Field label="Display Name" value={displayName} onChange={setDisplayName} />

      <div>
        <label className="text-[11px] text-white/55 mb-1 block">Modo</label>
        <div className="flex gap-2">
          {(['Sandbox', 'Live'] as const).map(m => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer transition
                          ${mode === m
                            ? 'bg-amber-500 text-[#0d141b]'
                            : 'bg-white/[0.04] text-white/70 hover:bg-white/[0.08]'}`}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      <Field label="Client ID" value={clientId} onChange={setClientId} mono />

      <SecretField
        label="Client Secret"
        value={clientSecret}
        onChange={setClientSecret}
        show={showSecret}
        onToggleShow={() => setShowSecret(v => !v)}
        placeholder={data.hasClientSecret ? '••••••• (mantém o atual se vazio)' : 'cole o secret aqui'}
      />

      <Actions busy={busy} onSave={save} />
    </div>
  );
}

// ----------------------------------------------------------------- shared bits

function Header({ title, enabled, onToggle, ready }: {
  title: string; enabled: boolean; onToggle: (v: boolean) => void; ready: boolean;
}) {
  return (
    <div className="flex items-center justify-between border-b border-white/[0.06] pb-4 mb-2">
      <div>
        <h3 className="text-lg font-bold text-white">{title}</h3>
        <p className="text-[11px] text-white/45 mt-0.5 flex items-center gap-1">
          {ready
            ? <><Check size={12} className="text-emerald-400" /> Credenciais OK</>
            : <><X size={12} className="text-amber-400" /> Faltam credenciais</>}
        </p>
      </div>
      <Toggle label={enabled ? 'Ativo' : 'Inativo'} value={enabled} onChange={onToggle} large />
    </div>
  );
}

function Toggle({ label, value, onChange, large }: {
  label: string; value: boolean; onChange: (v: boolean) => void; large?: boolean;
}) {
  return (
    <label className="flex items-center gap-2 cursor-pointer select-none">
      <button
        type="button"
        role="switch"
        aria-checked={value}
        onClick={() => onChange(!value)}
        className={`relative rounded-full transition cursor-pointer
                    ${large ? 'w-12 h-6' : 'w-10 h-5'}
                    ${value ? 'bg-emerald-500' : 'bg-white/[0.10]'}`}
      >
        <span className={`absolute top-0.5 bg-white rounded-full transition-all
                          ${large ? 'w-5 h-5' : 'w-4 h-4'}
                          ${value
                            ? (large ? 'left-6' : 'left-5')
                            : 'left-0.5'}`} />
      </button>
      <span className="text-sm text-white/75">{label}</span>
    </label>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>;
}

function Field({
  label, value, onChange, mono,
}: { label: string; value: string; onChange: (v: string) => void; mono?: boolean }) {
  return (
    <div>
      <label className="text-[11px] text-white/55 mb-1 block">{label}</label>
      <input
        type="text" value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white ${mono ? 'font-mono' : ''}`}
      />
    </div>
  );
}

function SecretField({
  label, value, onChange, show, onToggleShow, placeholder,
}: {
  label: string; value: string; onChange: (v: string) => void;
  show: boolean; onToggleShow: () => void; placeholder: string;
}) {
  return (
    <div>
      <label className="text-[11px] text-white/55 mb-1 block">{label}</label>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 pr-10 text-sm text-white font-mono"
        />
        <button
          type="button"
          onClick={onToggleShow}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-white/55 hover:text-white p-1 cursor-pointer"
          title={show ? 'Ocultar' : 'Mostrar'}
        >
          {show ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
      </div>
    </div>
  );
}

function Actions({ busy, onSave, onTest, testLabel }: {
  busy: boolean; onSave: () => void; onTest?: () => void; testLabel?: string;
}) {
  return (
    <div className="flex gap-2 pt-2">
      <button
        onClick={onSave}
        disabled={busy}
        className="flex-1 px-4 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-400
                   text-[#0d141b] font-bold text-sm transition cursor-pointer
                   disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {busy && <Loader2 size={14} className="animate-spin" />}
        Salvar alterações
      </button>
      {onTest && (
        <button
          onClick={onTest}
          disabled={busy}
          className="px-4 py-2.5 rounded-lg bg-white/[0.04] hover:bg-white/[0.08]
                     text-white/85 font-semibold text-sm transition cursor-pointer
                     disabled:opacity-50 flex items-center gap-2"
        >
          <RefreshCw size={14} />
          {testLabel || 'Testar'}
        </button>
      )}
    </div>
  );
}
